import { memo, useId } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { Button } from '@/components/Button';
import { Input } from '@/components/_inputs/Input';

import {
  type ConnectionsRiskFilterCategory,
  CONNECTIONS_FILTER_RISK_OPTIONS,
} from './CounterpartyConnections.helpers';

import styles from './CounterpartyConnections.module.sass';

export type CounterpartyConnectionsFilterPanelProps = {
  search: string;
  onSearchChange: (value: string) => void;
  riskCategory: ConnectionsRiskFilterCategory;
  onRiskCategoryChange: (value: ConnectionsRiskFilterCategory) => void;
  filterActive: boolean;
  onClear: () => void;
};

export const CounterpartyConnectionsFilterPanel = memo(
  function CounterpartyConnectionsFilterPanel({
    search,
    onSearchChange,
    riskCategory,
    onRiskCategoryChange,
    filterActive,
    onClear,
  }: CounterpartyConnectionsFilterPanelProps) {
    const { t } = useTranslation();
    const filterSearchFieldId = useId();
    const filterRiskLabelId = useId();

    return (
      <div className={styles.filterPanel}>
        <div className={styles.filterPanelField}>
          <Input
            id={filterSearchFieldId}
            name="connectionsFilterSearch"
            type="text"
            className={styles.filterSearchInput}
            value={search}
            onChange={onSearchChange}
            placeholder={
              t('translation.connections_filter_search_placeholder') || ''
            }
          />
        </div>
        <div className={styles.filterPanelSection}>
          <div className={styles.filterPanelLabel} id={filterRiskLabelId}>
            {t('translation.connections_filter_risk_level')}
          </div>
          <div
            className={styles.filterChips}
            role="group"
            aria-labelledby={filterRiskLabelId}
          >
            {CONNECTIONS_FILTER_RISK_OPTIONS.map(
              ({ value, translationKey }) => (
                <button
                  key={value}
                  type="button"
                  className={cx(styles.filterChip, {
                    [styles.filterChipActive]: riskCategory === value,
                  })}
                  aria-pressed={riskCategory === value}
                  onClick={() => onRiskCategoryChange(value)}
                >
                  {t(translationKey)}
                </button>
              ),
            )}
          </div>
        </div>
        <div className={styles.filterPanelActions}>
          <Button
            type="secondary"
            size="s"
            disabled={!filterActive}
            onClick={onClear}
          >
            {t('translation.btn_clear')}
          </Button>
        </div>
      </div>
    );
  },
);
